import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./JobSeeker.css";
// bring in loan-apply-form styles so form matches home design exactly
import "../common/loan-apply-form/loan-apply-form.css";

const JobSeeker = () => {
  const navigate = useNavigate();

  // Form step state
  const [currentStep, setCurrentStep] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobile: "",
    panCard: null,
    aadhaarFront: null,
    aadhaarBack: null,
    profilePhoto: null,
    position: "",
    experience: "",
    currentCompany: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    coverLetter: "",
    linkedin: "",
    portfolio: "",
    terms: false,
  });

  const allowedDomains = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "icloud.com",
    "aol.com",
    "protonmail.com",
    "mail.com",
    "yandex.com",
    "rediffmail.com",
  ];

  // Validation errors state
  const [errors, setErrors] = useState({});
  const [showDomainSuggestions, setShowDomainSuggestions] = useState(false);
  const [localPart, setLocalPart] = useState("");
  const [filteredDomains, setFilteredDomains] = useState(allowedDomains);

  // File states
  const [resumeFile, setResumeFile] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [panCardFile, setPanCardFile] = useState(null);
  const [aadhaarFrontFile, setAadhaarFrontFile] = useState(null);
  const [aadhaarBackFile, setAadhaarBackFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [fileName, setFileName] = useState("");
  const [offerLetterFileName, setOfferLetterFileName] = useState("");
  const [panCardFileName, setPanCardFileName] = useState("");
  const [aadhaarFrontFileName, setAadhaarFrontFileName] = useState("");
  const [aadhaarBackFileName, setAadhaarBackFileName] = useState("");

  // Additional dynamic sections
  const [additionalDocs, setAdditionalDocs] = useState([]);
  const [salarySlips, setSalarySlips] = useState([
    { id: Date.now(), month: "", file: null, fileName: "" },
  ]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");

  // Validation function (can be scoped by step)
  const validateForm = (step = currentStep) => {
    const newErrors = {};

    // step1: personal info only
    if (step === 1) {
      if (!formData.fullName.trim()) {
        newErrors.fullName = "Full name is required";
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!emailRegex.test(formData.email)) {
        newErrors.email = "Enter a valid email address";
      } else {
        const domain = formData.email.split("@")[1];
        if (!allowedDomains.includes(domain)) {
          newErrors.email =
            "Please use an email from a supported provider (e.g., gmail.com, yahoo.com, outlook.com)";
        }
      }

      const mobileRegex = /^[0-9]{10}$/;
      if (!formData.mobile.trim()) {
        newErrors.mobile = "Mobile number is required";
      } else if (!mobileRegex.test(formData.mobile)) {
        newErrors.mobile = "Enter a valid 10-digit mobile number";
      }

      if (!formData.position) {
        newErrors.position = "Please select a position";
      }

      if (!formData.experience) {
        newErrors.experience = "Please enter total experience";
      }

      if (!formData.currentCTC) {
        newErrors.currentCTC = "Please enter current CTC";
      }

      if (!formData.expectedCTC) {
        newErrors.expectedCTC = "Please enter expected CTC";
      }

      if (!formData.noticePeriod) {
        newErrors.noticePeriod = "Please select notice period duration";
      }
    }

    // step2: document uploads and terms
    if (step === 2) {
      if (!resumeFile) {
        newErrors.resume = "Resume is required";
      }

      // Profile photo validation - REQUIRED
      if (!profilePhotoFile) {
        newErrors.profilePhoto = "Profile photo is required";
      } else {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(profilePhotoFile.type)) {
          newErrors.profilePhoto = "Please upload only JPG or PNG images";
        } else if (profilePhotoFile.size > 2 * 1024 * 1024) {
          newErrors.profilePhoto = "Profile photo must be less than 2MB";
        }
      }

      // PAN card validation
      if (panCardFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(panCardFile.type)) {
          newErrors.panCard = "Please upload only JPG or PNG images";
        } else if (panCardFile.size > 2 * 1024 * 1024) {
          newErrors.panCard = "PAN card image must be less than 2MB";
        }
      }

      // Aadhaar front validation
      if (aadhaarFrontFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(aadhaarFrontFile.type)) {
          newErrors.aadhaarFront = "Please upload only JPG or PNG images";
        } else if (aadhaarFrontFile.size > 2 * 1024 * 1024) {
          newErrors.aadhaarFront = "Aadhaar front image must be less than 2MB";
        }
      }

      // Aadhaar back validation
      if (aadhaarBackFile) {
        const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
        if (!allowedTypes.includes(aadhaarBackFile.type)) {
          newErrors.aadhaarBack = "Please upload only JPG or PNG images";
        } else if (aadhaarBackFile.size > 2 * 1024 * 1024) {
          newErrors.aadhaarBack = "Aadhaar back image must be less than 2MB";
        }
      }

      // Offer letter validation
      if (offerLetterFile) {
        const allowedTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];
        if (!allowedTypes.includes(offerLetterFile.type)) {
          newErrors.offerLetter = "Please upload only PDF, DOC or DOCX files";
        } else if (offerLetterFile.size > 5 * 1024 * 1024) {
          newErrors.offerLetter = "Offer letter must be less than 5MB";
        }
      }

      if (!formData.terms) {
        newErrors.terms = "You must agree to the terms";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return errors[fieldName] ? "error" : "";
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // For mobile field, only allow numbers
    let finalValue = value;
    if (name === "mobile") {
      finalValue = value.replace(/[^0-9]/g, "");
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : finalValue,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle domain suggestion click
  const handleDomainSelect = (domain) => {
    setFormData((prev) => ({ ...prev, email: localPart + "@" + domain }));
    setShowDomainSuggestions(false);
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, email: value }));

    if (errors.email) setErrors((prev) => ({ ...prev, email: "" }));
  };

  // Currency formatting
  const formatCurrency = (value) => {
    const numericValue = value.replace(/[^\d]/g, "");
    if (!numericValue) return "";
    return parseInt(numericValue).toLocaleString("en-IN");
  };

  const handleCurrencyChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = formatCurrency(value);
    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  // File handlers (similar to original but with validation)
  const handleFileChange = (
    e,
    setter,
    setFileName,
    allowedTypes,
    maxSize,
    fieldName = "",
  ) => {
    const file = e.target.files[0];
    if (!file) {
      setter(null);
      setFileName("");
      return;
    }

    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage(
        `Please upload only ${allowedTypes.map((t) => t.split("/")[1]).join(", ")} files`,
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > maxSize) {
      setErrorModalMessage(
        `File size must be less than ${maxSize / (1024 * 1024)}MB`,
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setter(file);
    setFileName(
      `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
    );

    // Clear resume error if it was there
    if (fieldName === "resume") {
      setErrors((prev) => ({ ...prev, resume: "" }));
    }
  };

  // Resume specific handler
  const handleResumeChange = (e) => {
    handleFileChange(
      e,
      setResumeFile,
      setFileName,
      [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      5 * 1024 * 1024,
      "resume",
    );
  };

  // PAN card handler
  const handlePanCardChange = (e) => {
    handleFileChange(
      e,
      setPanCardFile,
      setPanCardFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    // Update formData with file name (optional)
    if (e.target.files[0]) {
      setFormData((prev) => ({ ...prev, panCard: e.target.files[0].name }));
    }
  };

  // Aadhaar front handler
  const handleAadhaarFrontChange = (e) => {
    handleFileChange(
      e,
      setAadhaarFrontFile,
      setAadhaarFrontFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    if (e.target.files[0]) {
      setFormData((prev) => ({
        ...prev,
        aadhaarFront: e.target.files[0].name,
      }));
    }
  };

  // Aadhaar back handler
  const handleAadhaarBackChange = (e) => {
    handleFileChange(
      e,
      setAadhaarBackFile,
      setAadhaarBackFileName,
      ["image/jpeg", "image/jpg", "image/png"],
      2 * 1024 * 1024,
    );
    if (e.target.files[0]) {
      setFormData((prev) => ({ ...prev, aadhaarBack: e.target.files[0].name }));
    }
  };

  // Offer letter handler
  const handleOfferLetterChange = (e) => {
    handleFileChange(
      e,
      setOfferLetterFile,
      setOfferLetterFileName,
      [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      5 * 1024 * 1024,
    );
  };

  // Profile photo upload via file input (simplified, removed camera)
  const handleProfilePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage("Please upload only JPG or PNG images");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrorModalMessage("Profile photo must be less than 2MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setProfilePhotoFile(file);
    setFormData((prev) => ({ ...prev, profilePhoto: file.name }));
    setProfilePhotoPreview(URL.createObjectURL(file));

    // Clear error if it was there
    if (errors.profilePhoto) {
      setErrors((prev) => ({ ...prev, profilePhoto: "" }));
    }
  };

  // Additional Documents
  const addAdditionalDocument = () => {
    setAdditionalDocs([
      ...additionalDocs,
      { id: Date.now(), type: "", file: null, fileName: "" },
    ]);
  };

  const removeAdditionalDocument = (id) => {
    setAdditionalDocs(additionalDocs.filter((doc) => doc.id !== id));
  };

  const handleAdditionalDocTypeChange = (id, value) => {
    setAdditionalDocs(
      additionalDocs.map((doc) =>
        doc.id === id ? { ...doc, type: value } : doc,
      ),
    );
  };

  const handleAdditionalDocFileChange = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage(
        "Please upload only PDF, DOC, DOCX, JPG or PNG files",
      );
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorModalMessage("File size must be less than 5MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setAdditionalDocs(
      additionalDocs.map((doc) =>
        doc.id === id
          ? {
              ...doc,
              file,
              fileName: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
            }
          : doc,
      ),
    );
  };

  // Salary Slips
  const addSalarySlip = () => {
    if (salarySlips.length >= 6) return;
    setSalarySlips([
      ...salarySlips,
      { id: Date.now(), month: "", file: null, fileName: "" },
    ]);
  };

  const removeSalarySlip = (id) => {
    if (salarySlips.length <= 1) return;
    setSalarySlips(salarySlips.filter((slip) => slip.id !== id));
  };

  const handleSalarySlipMonthChange = (id, value) => {
    setSalarySlips(
      salarySlips.map((slip) =>
        slip.id === id ? { ...slip, month: value } : slip,
      ),
    );
  };

  const handleSalarySlipFileChange = (id, e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ];
    if (!allowedTypes.includes(file.type)) {
      setErrorModalMessage("Please upload only PDF, JPG or PNG files");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrorModalMessage("File size must be less than 5MB");
      setShowErrorModal(true);
      e.target.value = "";
      return;
    }

    setSalarySlips(
      salarySlips.map((slip) =>
        slip.id === id
          ? {
              ...slip,
              file,
              fileName: `Selected: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)} MB)`,
            }
          : slip,
      ),
    );
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      mobile: "",
      panCard: null,
      aadhaarFront: null,
      aadhaarBack: null,
      profilePhoto: null,
      position: "",
      experience: "",
      currentCompany: "",
      currentCTC: "",
      expectedCTC: "",
      noticePeriod: "",
      coverLetter: "",
      linkedin: "",
      portfolio: "",
      terms: false,
    });
    setResumeFile(null);
    setProfilePhotoFile(null);
    setOfferLetterFile(null);
    setPanCardFile(null);
    setAadhaarFrontFile(null);
    setAadhaarBackFile(null);
    setProfilePhotoPreview("");
    setFileName("");
    setOfferLetterFileName("");
    setPanCardFileName("");
    setAadhaarFrontFileName("");
    setAadhaarBackFileName("");
    setAdditionalDocs([]);
    setSalarySlips([{ id: Date.now(), month: "", file: null, fileName: "" }]);
    setErrors({});
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // when using page, we only submit when on final step
    if (currentStep !== 2) {
      if (validateForm()) {
        setCurrentStep(2);
      }
      return;
    }

    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const element = document.getElementById(firstError);
      if (element)
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setLoading(true);

    const submitData = new FormData();
    submitData.append("name", formData.fullName);
    submitData.append("email", formData.email);
    submitData.append("mobile_number", formData.mobile);
    submitData.append("position_applying_for", formData.position);
    if (formData.experience)
      submitData.append("total_experience_years", formData.experience);
    if (formData.currentCompany)
      submitData.append("current_company", formData.currentCompany);
    if (formData.currentCTC)
      submitData.append(
        "current_ctc",
        formData.currentCTC.replace(/[^\d]/g, "") || "0",
      );
    if (formData.expectedCTC)
      submitData.append(
        "expected_ctc",
        formData.expectedCTC.replace(/[^\d]/g, "") || "0",
      );
    if (formData.noticePeriod)
      submitData.append("notice_period", formData.noticePeriod);
    if (formData.coverLetter)
      submitData.append("cover_letter", formData.coverLetter);
    if (formData.linkedin)
      submitData.append("linkedin_profile", formData.linkedin);
    if (formData.portfolio)
      submitData.append("portfolio_website", formData.portfolio);
    submitData.append("agreed", formData.terms ? "1" : "0");

    if (resumeFile) submitData.append("resume", resumeFile);
    if (profilePhotoFile) submitData.append("selfie", profilePhotoFile);
    if (offerLetterFile)
      submitData.append("other_documents[]", offerLetterFile);
    if (panCardFile) submitData.append("pan_card", panCardFile);
    if (aadhaarFrontFile)
      submitData.append("aadhar_card_front", aadhaarFrontFile);
    if (aadhaarBackFile) submitData.append("aadhar_card_back", aadhaarBackFile);

    salarySlips.forEach((slip, index) => {
      if (slip.file) submitData.append(`salary_slips[${index}]`, slip.file);
    });

    additionalDocs.forEach((doc, index) => {
      if (doc.file) {
        submitData.append(`other_documents[${index}][file]`, doc.file);
        submitData.append(
          `other_documents[${index}][document_type]`,
          doc.type || "Other",
        );
        submitData.append(
          `other_documents[${index}][document_name]`,
          doc.file.name,
        );
      }
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/job-seeker/register`,
        {
          method: "POST",
          body: submitData,
        },
      );

      const result = await response.json();

      if (response.ok) {
        setShowSuccessModal(true);
        resetForm();
      } else {
        let errorMessage = "Error submitting application. Please try again.";
        if (result.errors) {
          const firstErrorKey = Object.keys(result.errors)[0];
          const firstError = result.errors[firstErrorKey];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else if (result.message) {
          errorMessage = result.message;
        }
        setErrorModalMessage(errorMessage);
        setShowErrorModal(true);
      }
    } catch (error) {
      setErrorModalMessage(
        "Network error. Please check your connection and try again.",
      );
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  // navigation helpers for steps
  const goToNextStep = () => {
    if (validateForm()) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPrevStep = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo(0, 0);
  };

  return (
    <div className="job-seeker-container">
      {/* Back to Home Button */}
      <button
        className="job-seeker-back-home-btn"
        onClick={() => navigate("/")}
      >
        <i className="fas fa-home"></i> Back to Home
      </button>

     

      {/* Main Form Section */}
      <section className="job-seeker-form-section">
        <div className="job-seeker-container">
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>

            {/* Step Header */}
            <div className="job-seeker-step-header">
              <h2 className="job-seeker-step-title">
                {currentStep === 1 ? "Personal Information" : "Upload Documents"}
              </h2>
              <p className="job-seeker-step-description">
                {currentStep === 1 
                  ? "Please fill in your personal details accurately" 
                  : "Upload required documents to complete your application"
                }
              </p>
            </div>

            {/* Progress Stepper */}
            <div className="job-seeker-stepper">
              {[1, 2].map((step) => (
                <div key={step} className="job-seeker-step-container">
                  <div className={`job-seeker-step-item ${currentStep >= step ? "active" : ""}`}>
                    <div className="job-seeker-step-circle">
                      {currentStep > step ? (
                        <i className="fas fa-check"></i>
                      ) : (
                        step
                      )}
                    </div>
                    <div className="job-seeker-step-label">
                      {step === 1 ? "Personal Info" : "Documents"}
                    </div>
                  </div>
                  {step < 2 && (
                    <div className={`job-seeker-step-line ${currentStep > step ? "completed" : ""}`}></div>
                  )}
                </div>
              ))}
            </div>

            <div className="ln-app-form">
              <form
                id="jobSeekerForm"
                className="ln-app-form"
                onSubmit={handleSubmit}
                noValidate
              >
                {/* step 1 personal info */}
                {currentStep === 1 && (
                  <div className="ln-app-step-content animation-fade">
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Full Name (As Per Pan Card){" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="fullName"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`ln-app-input ${getErrorClass("fullName")}`}
                        placeholder="Rajesh Sharma"
                        style={
                          errors.fullName
                            ? { borderColor: "#ef4444", borderWidth: "2px" }
                            : {}
                        }
                      />
                      {errors.fullName && (
                        <span className="ln-app-error">{errors.fullName}</span>
                      )}
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Email Address{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleEmailChange}
                          className={`ln-app-input ${getErrorClass("email")}`}
                          placeholder="example@mail.com"
                          autoComplete="off"
                          style={
                            errors.email
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.email && (
                          <span className="ln-app-error">{errors.email}</span>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Mobile Number{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          value={formData.mobile}
                          onChange={handleInputChange}
                          className={`ln-app-input ${getErrorClass("mobile")}`}
                          placeholder="9737304440"
                          maxLength="10"
                          pattern="[0-9]*"
                          inputMode="numeric"
                          onKeyPress={(e) => {
                            if (!/[0-9]/.test(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          style={
                            errors.mobile
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.mobile && (
                          <span className="ln-app-error">{errors.mobile}</span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Position Applying For{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <select
                          id="position"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          className={`ln-app-select ${getErrorClass("position")}`}
                          style={
                            errors.position
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        >
                          <option value="">Select Position</option>
                          <option value="Loan Sales Executive">Loan Sales Executive</option>
                          <option value="Relationship Manager">Relationship Manager</option>
                          <option value="Business Development Manager">Business Development Manager</option>
                          <option value="Operations Executive">Operations Executive</option>
                          <option value="Credit Analyst">Credit Analyst</option>
                          <option value="Customer Support Executive">Customer Support Executive</option>
                        </select>
                        {errors.position && (
                          <span className="ln-app-error">{errors.position}</span>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Total Experience{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <select
                          id="experience"
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                          className={`ln-app-select ${errors.experience ? "error" : ""}`}
                          style={
                            errors.experience
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        >
                          <option value="">Select Experience</option>
                          <option value="Fresher">Fresher (0-1 years)</option>
                          <option value="1-3">1-3 years</option>
                          <option value="3-5">3-5 years</option>
                          <option value="5-8">5-8 years</option>
                          <option value="8+">8+ years</option>
                        </select>
                        {errors.experience && (
                          <span className="ln-app-error">
                            {errors.experience}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">Current Company</label>
                        <input
                          type="text"
                          id="currentCompany"
                          name="currentCompany"
                          value={formData.currentCompany}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="Current company name"
                        />
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Current CTC (₹){" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="text"
                          id="currentCTC"
                          name="currentCTC"
                          value={formData.currentCTC}
                          onChange={handleCurrencyChange}
                          className={`ln-app-input ${getErrorClass("currentCTC")}`}
                          placeholder="e.g., 5,00,000"
                          style={
                            errors.currentCTC
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.currentCTC && (
                          <span className="ln-app-error">
                            {errors.currentCTC}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Expected CTC (₹){" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="text"
                          id="expectedCTC"
                          name="expectedCTC"
                          value={formData.expectedCTC}
                          onChange={handleCurrencyChange}
                          className={`ln-app-input ${getErrorClass("expectedCTC")}`}
                          placeholder="e.g., 7,00,000"
                          style={
                            errors.expectedCTC
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        />
                        {errors.expectedCTC && (
                          <span className="ln-app-error">
                            {errors.expectedCTC}
                          </span>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Notice Period{" "}
                          <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <select
                          id="noticePeriod"
                          name="noticePeriod"
                          value={formData.noticePeriod}
                          onChange={handleInputChange}
                          className={`ln-app-select ${getErrorClass("noticePeriod")}`}
                          style={
                            errors.noticePeriod
                              ? { borderColor: "#ef4444", borderWidth: "2px" }
                              : {}
                          }
                        >
                          <option value="">Select Notice Period</option>
                          <option value="Immediate">Immediate Joining</option>
                          <option value="15">15 days</option>
                          <option value="30">30 days</option>
                          <option value="45">45 days</option>
                          <option value="60">60 days</option>
                          <option value="90">90 days</option>
                        </select>
                        {errors.noticePeriod && (
                          <span className="ln-app-error">
                            {errors.noticePeriod}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* step 2 documents */}
                {currentStep === 2 && (
                  <div className="ln-app-step-content animation-fade">
                    {/* Resume Upload */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Upload Resume{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <div
                        className="ln-app-file-upload"
                        style={{
                          border: errors.resume
                            ? "2px dashed #ef4444"
                            : "2px dashed #cbd5e1",
                          padding: "20px",
                          borderRadius: "10px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="file"
                          id="resume"
                          accept=".pdf,.doc,.docx"
                          onChange={handleResumeChange}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor="resume"
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          <i
                            className="fas fa-cloud-upload-alt"
                            style={{
                              fontSize: "24px",
                              color: errors.resume ? "#ef4444" : "#4F46E5",
                              marginBottom: "10px",
                            }}
                          ></i>
                          <p
                            style={{
                              margin: "10px 0",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            Click to upload your resume
                          </p>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            PDF, DOC or DOCX (Max 5MB)
                          </p>
                        </label>
                      </div>
                      {fileName && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#10b981",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {fileName}
                        </p>
                      )}
                      {errors.resume && (
                        <span className="ln-app-error">{errors.resume}</span>
                      )}
                    </div>

                    {/* Profile Photo */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">
                        Profile Photo{" "}
                        <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          alignItems: "center",
                        }}
                      >
                        <div
                          style={{
                            width: "100px",
                            height: "100px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            border: errors.profilePhoto
                              ? "2px dashed #ef4444"
                              : "2px dashed #cbd5e1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "#f8fafc",
                          }}
                        >
                          {profilePhotoPreview ? (
                            <img
                              src={profilePhotoPreview}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <i
                              className="fas fa-user"
                              style={{
                                fontSize: "40px",
                                color: errors.profilePhoto
                                  ? "#ef4444"
                                  : "#94a3b8",
                              }}
                            ></i>
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            className="ln-app-file-upload"
                            style={{
                              border: errors.profilePhoto
                                ? "2px dashed #ef4444"
                                : "2px dashed #cbd5e1",
                              padding: "15px",
                              borderRadius: "10px",
                              textAlign: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="file"
                              id="profilePhoto"
                              accept="image/*"
                              onChange={handleProfilePhotoChange}
                              style={{ display: "none" }}
                            />
                            <label
                              htmlFor="profilePhoto"
                              style={{ cursor: "pointer", display: "block" }}
                            >
                              <i
                                className="fas fa-camera"
                                style={{
                                  fontSize: "20px",
                                  color: errors.profilePhoto
                                    ? "#ef4444"
                                    : "#4F46E5",
                                  marginBottom: "8px",
                                }}
                              ></i>
                              <p
                                style={{
                                  margin: "8px 0",
                                  color: "#475569",
                                  fontWeight: "500",
                                  fontSize: "14px",
                                }}
                              >
                                Upload Photo
                              </p>
                              <p
                                style={{
                                  margin: "5px 0",
                                  color: "#94a3b8",
                                  fontSize: "11px",
                                }}
                              >
                                JPG, PNG (Max 2MB)
                              </p>
                            </label>
                          </div>
                          {errors.profilePhoto && (
                            <p
                              style={{
                                marginTop: "8px",
                                color: "#ef4444",
                                fontSize: "12px",
                              }}
                            >
                              {errors.profilePhoto}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* PAN Card Upload */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">PAN Card</label>
                      <div
                        className="ln-app-file-upload"
                        style={{
                          border: "2px dashed #cbd5e1",
                          padding: "20px",
                          borderRadius: "10px",
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="file"
                          id="panCard"
                          accept="image/*"
                          onChange={handlePanCardChange}
                          style={{ display: "none" }}
                        />
                        <label
                          htmlFor="panCard"
                          style={{ cursor: "pointer", display: "block" }}
                        >
                          <i
                            className="fas fa-id-card"
                            style={{
                              fontSize: "24px",
                              color: "#4F46E5",
                              marginBottom: "10px",
                            }}
                          ></i>
                          <p
                            style={{
                              margin: "10px 0",
                              color: "#475569",
                              fontWeight: "500",
                            }}
                          >
                            Upload PAN Card
                          </p>
                          <p
                            style={{
                              margin: "5px 0",
                              color: "#94a3b8",
                              fontSize: "12px",
                            }}
                          >
                            JPG, PNG (Max 2MB)
                          </p>
                        </label>
                      </div>
                      {panCardFileName && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#10b981",
                            fontSize: "14px",
                          }}
                        >
                          ✓ {panCardFileName}
                        </p>
                      )}
                    </div>

                    {/* Aadhaar Card Front & Back */}
                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Aadhaar Card Front
                        </label>
                        <div
                          className="ln-app-file-upload"
                          style={{
                            border: "2px dashed #cbd5e1",
                            padding: "15px",
                            borderRadius: "10px",
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            id="aadhaarFront"
                            accept="image/*"
                            onChange={handleAadhaarFrontChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="aadhaarFront"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <i
                              className="fas fa-address-card"
                              style={{
                                fontSize: "20px",
                                color: "#4F46E5",
                                marginBottom: "8px",
                              }}
                            ></i>
                            <p
                              style={{
                                margin: "8px 0",
                                color: "#475569",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              Upload Front
                            </p>
                            <p
                              style={{
                                margin: "5px 0",
                                color: "#94a3b8",
                                fontSize: "11px",
                              }}
                            >
                              Max 2MB
                            </p>
                          </label>
                        </div>
                        {aadhaarFrontFileName && (
                          <p
                            style={{
                              marginTop: "8px",
                              color: "#10b981",
                              fontSize: "12px",
                            }}
                          >
                            ✓ {aadhaarFrontFileName}
                          </p>
                        )}
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Aadhaar Card Back
                        </label>
                        <div
                          className="ln-app-file-upload"
                          style={{
                            border: "2px dashed #cbd5e1",
                            padding: "15px",
                            borderRadius: "10px",
                            textAlign: "center",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            id="aadhaarBack"
                            accept="image/*"
                            onChange={handleAadhaarBackChange}
                            style={{ display: "none" }}
                          />
                          <label
                            htmlFor="aadhaarBack"
                            style={{ cursor: "pointer", display: "block" }}
                          >
                            <i
                              className="fas fa-address-card"
                              style={{
                                fontSize: "20px",
                                color: "#4F46E5",
                                marginBottom: "8px",
                              }}
                            ></i>
                            <p
                              style={{
                                margin: "8px 0",
                                color: "#475569",
                                fontWeight: "500",
                                fontSize: "14px",
                              }}
                            >
                              Upload Back
                            </p>
                            <p
                              style={{
                                margin: "5px 0",
                                color: "#94a3b8",
                                fontSize: "11px",
                              }}
                            >
                              Max 2MB
                            </p>
                          </label>
                        </div>
                        {aadhaarBackFileName && (
                          <p
                            style={{
                              marginTop: "8px",
                              color: "#10b981",
                              fontSize: "12px",
                            }}
                          >
                            ✓ {aadhaarBackFileName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Cover Letter */}
                    <div className="ln-app-group">
                      <label className="ln-app-label">Cover Letter</label>
                      <textarea
                        id="coverLetter"
                        name="coverLetter"
                        value={formData.coverLetter}
                        onChange={handleInputChange}
                        className="ln-app-input"
                        rows="4"
                        placeholder="Tell us why you're interested in this position..."
                        style={{ resize: "vertical", minHeight: "100px" }}
                      />
                    </div>

                    {/* LinkedIn & Portfolio */}
                    <div className="ln-app-row">
                      <div className="ln-app-group">
                        <label className="ln-app-label">LinkedIn Profile</label>
                        <input
                          type="url"
                          id="linkedin"
                          name="linkedin"
                          value={formData.linkedin}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="https://linkedin.com/in/yourprofile"
                        />
                      </div>
                      <div className="ln-app-group">
                        <label className="ln-app-label">
                          Portfolio/Website
                        </label>
                        <input
                          type="url"
                          id="portfolio"
                          name="portfolio"
                          value={formData.portfolio}
                          onChange={handleInputChange}
                          className="ln-app-input"
                          placeholder="https://yourportfolio.com"
                        />
                      </div>
                    </div>

                    {/* Terms & Conditions */}
                    <div className="ln-app-group">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: "12px",
                          padding: "15px",
                          background: "#f1f5f9",
                          borderRadius: "10px",
                          border: errors.terms
                            ? "2px solid #ef4444"
                            : "2px solid transparent",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="terms"
                          name="terms"
                          checked={formData.terms}
                          onChange={handleInputChange}
                          style={{
                            width: "18px",
                            height: "18px",
                            marginTop: "2px",
                            cursor: "pointer",
                            accentColor: errors.terms ? "#ef4444" : "#4F46E5",
                          }}
                        />
                        <label
                          htmlFor="terms"
                          style={{
                            fontSize: "13px",
                            color: "#475569",
                            lineHeight: "1.5",
                            cursor: "pointer",
                            margin: 0,
                          }}
                        >
                          I agree to the{" "}
                          <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#4F46E5", textDecoration: "none" }}
                          >
                            Privacy Policy
                          </a>{" "}
                          and consent to being contacted for recruitment
                          purposes <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                      </div>
                      {errors.terms && (
                        <span className="ln-app-error">{errors.terms}</span>
                      )}
                    </div>
                  </div>
                )}

                {/* navigation buttons at bottom of form */}
                <div className="ln-app-actions">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={goToPrevStep}
                      className="ln-btn-secondary"
                    >
                      ← Back
                    </button>
                  )}
                  <div className="ln-spacer"></div>
                  {currentStep < 2 && (
                    <button
                      type="button"
                      onClick={goToNextStep}
                      className="ln-btn-primary"
                    >
                      Next →
                    </button>
                  )}
                  {currentStep === 2 && (
                    <button
                      type="submit"
                      className="ln-btn-primary"
                      disabled={loading}
                    >
                      {loading ? "Submitting..." : "Submit Application"}
                    </button>
                  )}
                </div>
              </form>
            </div>
        </div>
        </div>
      </section>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="job-seeker-modal" style={{ display: "flex" }}>
          <div className="job-seeker-modal-content">
            <div className="job-seeker-modal-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <h3>Application Submitted Successfully!</h3>
            <p>
              Thank you! Your application has been submitted successfully. Our
              HR team will contact you within 3-5 working days.
            </p>
            <div className="job-seeker-modal-actions">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="job-seeker-modal-btn"
              >
                Close
              </button>
              <button
                onClick={() => navigate("/")}
                className="job-seeker-modal-btn job-seeker-secondary"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="job-seeker-modal" style={{ display: "flex" }}>
          <div className="job-seeker-modal-content" style={{ maxWidth: "500px" }}>
            <div className="job-seeker-modal-icon" style={{ color: "#ef4444" }}>
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h3>Oops! Something Went Wrong</h3>
            <p
              style={{
                color: "#64748b",
                lineHeight: "1.6",
                marginBottom: "10px",
              }}
            >
              {errorModalMessage}
            </p>
            <div className="job-seeker-modal-actions">
              <button
                onClick={() => setShowErrorModal(false)}
                className="job-seeker-modal-btn"
                style={{
                  background: "#ef4444",
                  borderColor: "#ef4444",
                  color: "white",
                }}
              >
                <i className="fas fa-times"></i> Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobSeeker;